import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import cn from "clsx";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import { isArabic } from "@/util";
import { Product } from "@/services/productService";
import PlpAnalytics from "@/services/analytics/main/plp";
import QuicklookModal from "@/components/quicklookModal";
import ListItem from "../listItem";
import { useLongLanguage } from "@/hooks";
import { Impressions, MetaData } from "../types";
import "./style.scss";

const ListItemWrapper: FC<{
  products: Product[];
  listHeading?: string;
  meta: MetaData;
}> = ({ products, listHeading, meta }) => {
  const language = useLongLanguage();
  const listingRef = useRef<HTMLUListElement | null>(null);
  const productImpressionsRef = useRef<Impressions>({});
  const recordsPassedRef = useRef(0);
  const [showQuicklookModal, setShowQuicklookModal] = useState(false);
  const [selectedQuicklookIndex, setSelectedQuicklookIndex] = useState<
    null | number
  >(null);

  const toggleQuicklook = useCallback(
    (
      toggleState: boolean,
      selectedIndex: number | null,
      event?: React.MouseEvent<HTMLElement>
    ) => {
      setSelectedQuicklookIndex(selectedIndex);
      setShowQuicklookModal(toggleState);

      if (event) event.stopPropagation();
    },
    []
  );

  const handelSwiperButtonClick = useCallback(
    (value: any) => {
      setSelectedQuicklookIndex(value);
      if (products[value]) {
        PlpAnalytics.select({
          item: { ...products[value], position: value },
          listHeading
        });
      }
    },
    [products, listHeading]
  );

  useEffect(() => {
    productImpressionsRef.current = {};
    recordsPassedRef.current = 0;
  }, [products]);

  useEffect(() => {
    const handleListingObservation = debounce(() => {
      const listRef = listingRef.current;
      if (!isEmpty(products) && listRef) {
        const firstChild = listRef.children?.[0] as HTMLElement | undefined;
        if (!firstChild) return;

        const { width: containerWidth } = listRef.getBoundingClientRect();
        const { width: elementWidth, height: elementHeight } =
          firstChild.getBoundingClientRect();
        if (!elementWidth || !elementHeight) return;

        const itemPerRow = Math.floor(containerWidth / elementWidth);
        if (!itemPerRow) return;

        const _scrolledItem =
          Math.floor(window.pageYOffset / elementHeight) * itemPerRow;

        const startIndex = _scrolledItem || 0;
        const lastIndex = _scrolledItem + itemPerRow - 1 || 0;
        if (
          lastIndex < products.length &&
          startIndex <= recordsPassedRef.current
        ) {
          let newImpressions: Impressions = {};
          for (let i = startIndex; i <= lastIndex; i++) {
            if (
              products[i] &&
              products[i].id &&
              !productImpressionsRef.current.hasOwnProperty(products[i].id)
            ) {
              newImpressions[products[i].id] = {
                ...products[i],
                position: i + 1
              };
            }
          }
          if (!isEmpty(newImpressions)) {
            const impressionsByIndex = [...Object.values(newImpressions)].sort(
              (a, b) => a.position - b.position
            );
            PlpAnalytics.impressions({
              items: impressionsByIndex,
              listHeading: meta?.heading1?.defaultValue
            });

            productImpressionsRef.current = {
              ...productImpressionsRef.current,
              ...newImpressions
            };
          }
        }
        recordsPassedRef.current =
          _scrolledItem + itemPerRow > products.length
            ? products.length
            : _scrolledItem + itemPerRow;
      }
    }, 200);
    window.addEventListener("scroll", handleListingObservation, {
      passive: true
    });
    handleListingObservation();

    return () => {
      window.removeEventListener("scroll", handleListingObservation);
      handleListingObservation.cancel();
    };
  }, [products, meta?.heading1?.defaultValue]);

  const productItemList = useMemo(
    () =>
      products?.map((item: Product, index: number) => (
        <ListItem
          item={item}
          key={item.id}
          position={index}
          listHeading={listHeading}
          toggleQuicklook={toggleQuicklook}
        />
      )),
    [products, listHeading, toggleQuicklook]
  );

  return (
    <div className="container">
      <ul
        className={cn("list_item_wrapper", {
          arabic: isArabic(language)
        })}
        ref={listingRef}
      >
        {productItemList}
      </ul>
      {showQuicklookModal && (
        <QuicklookModal
          showQuicklookModal={showQuicklookModal}
          toggleQuicklook={toggleQuicklook}
          selectedIndex={selectedQuicklookIndex}
          products={products}
          handelSwiperButtonClick={handelSwiperButtonClick}
        />
      )}
    </div>
  );
};

export default ListItemWrapper;
